import { Checkbox, Tooltip } from '@mm/components';
import CompanyModel from '@mm/state/model/CompanyModel';
import { StoreState } from '@mm/state/store-interfaces';
import { Formik, FormikProps } from 'formik';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import FormFieldWrapper from '../../../../../../../common/components/FormFieldWrapper/FormFieldWrapper';
import { PhoneInput } from '../../../../../../../common/components/PhoneInput/PhoneInput';
import { WrappedTextInput } from '../../../../../../../common/components/WrappedTextInput/WrappedTextInput';
import { getDeviceInfo } from '../../../../../../../common/hooks/use-device-info';
import { useResolutionInfo } from '@mm/hooks';
import CompleteRegisterButton from '../../../2-UserInfo/CompleteRegisterButton/CompleteRegisterButton';
import {
  fields,
  getMainFiledValidationSchema,
  UserInfoFormValues,
} from '../../../2-UserInfo/user-info-form-helpers';
import './WelcomePage.scss';
import classNames from 'classnames';
import PrimarySelect, {
  DropdownOption,
} from '@mm/components/legacy-components/PrimarySelect/PrimarySelect';

type Props = {
  onStartPress: (
    companyName: string,
    businessEmail: string,
    companyWebsite: string,
    phoneNumber: string,
    businessType?: string,
    smsConsent?: boolean,
    isAgency?: boolean,
    isUsingHighLevel?: boolean,
  ) => void;
  company: CompanyModel;
};

export const WelcomePage = (props: Props) => {
  const { onStartPress, company } = props;
  const { isMobile } = useResolutionInfo();
  const { isAndroid, isIOS } = getDeviceInfo();
  const userCountry = useSelector((store: StoreState) => store.env.userCountry);

  const businessTypeOptions = [
    { id: 'dtc', name: 'Direct to Consumer' },
    { id: 'b2b', name: 'Business to Business' },
    { id: 'agency', name: 'Agency' },
  ] as DropdownOption[];

  const [agency, setAgency] = useState(false);
  const [useHighLevel, setUseHighLevel] = useState(false);
  const [isSmsMarketingChecked, setIsSmsMarketingChecked] = useState(false);
  const [businessType, setBusinessType] = useState<DropdownOption | undefined>(
    undefined,
  );

  const validationSchema = getMainFiledValidationSchema();
  const initialValues = {
    businessEmail: company.businessEmail ?? '',
    companyName: '',
    companyWebsite: company.website ?? '',
    phoneNumber: company.phoneNumber ?? '',
    businessType: company.businessType ?? '',
  };

  const onSignupClick = async (formValues: UserInfoFormValues) => {
    const {
      businessEmail,
      companyName,
      companyWebsite,
      phoneNumber,
      businessType,
    } = formValues;

    onStartPress(
      companyName,
      businessEmail,
      companyWebsite,
      phoneNumber,
      businessType,
      isSmsMarketingChecked,
      agency,
      useHighLevel,
    );
  };

  const getIsValid = (formProps: FormikProps<UserInfoFormValues>) => {
    if (isMobile) {
      return false;
    }
    return !formProps.isValid;
  };

  return (
    <div className="welcome_page_wrapper">
      <Formik
        onSubmit={onSignupClick}
        initialValues={initialValues}
        validationSchema={validationSchema}
        // Until the below issue is fixed on mobile only validate on submit
        // due to autocomplete issues with certain combos of Android/Chrome
        // https://github.com/facebook/react/issues/1159
        validateOnChange={!isMobile}
        validateOnBlur={!isMobile}
        enableReinitialize
      >
        {(formProps: FormikProps<UserInfoFormValues>) => (
          <form onSubmit={() => onSignupClick(formProps.values)}>
            <div className="welcome-page__form-wrapper">
              <Tooltip
                placement="right"
                isWhite
                title="Please, list your website's URL that you would like to collect
                its visitor information and generate traffic to"
              >
                <WrappedTextInput
                  label="Company Website"
                  inputClassName="welcome-page__form-input"
                  formProps={formProps}
                  fieldName={fields.companyWebsite}
                />
              </Tooltip>

              <div className="welcome-page__inputs-row">
                <WrappedTextInput
                  label="Work Email"
                  inputClassName="welcome-page__form-input"
                  formProps={formProps}
                  fieldName={fields.businessEmail}
                />

                <FormFieldWrapper
                  className="welcome-page_form-field-wrapper"
                  label="Phone"
                  error={formProps.errors.phoneNumber}
                  touched={formProps.touched.phoneNumber}
                >
                  <PhoneInput
                    country={userCountry}
                    name={fields.phoneNumber}
                    className="user-info__phone-input"
                    value={formProps.values.phoneNumber}
                    onChange={value =>
                      formProps.setFieldValue('phoneNumber', value)
                    }
                    onBlur={() => formProps.setFieldTouched('phoneNumber')}
                    errorState={
                      formProps.touched.phoneNumber &&
                      !!formProps.errors.phoneNumber
                    }
                  />
                </FormFieldWrapper>
              </div>
              <div className="welcome-page__inputs-row">
                <WrappedTextInput
                  label="Company Name"
                  inputClassName="welcome-page__form-input"
                  formProps={formProps}
                  fieldName={fields.companyName}
                />

                <FormFieldWrapper
                  className="welcome-page_form-field-wrapper"
                  label="Business Type"
                  error={formProps.errors.businessType}
                  touched={true}
                >
                  <PrimarySelect
                    className={classNames('welcome-page__select', {
                      'welcome-page__select-error':
                        formProps.errors.businessType,
                    })}
                    value={businessType}
                    onChange={v => {
                      formProps.setFieldValue('businessType', v.id);

                      if (agency) {
                        setUseHighLevel(false);
                      }
                      setAgency(v.id === 'agency');
                      setBusinessType(v);
                    }}
                    options={businessTypeOptions}
                  />
                </FormFieldWrapper>
              </div>
              <div className="welcome-page__input-group">
                {agency && (
                  <Checkbox
                    className="sign-up-form__agency-checkbox"
                    isChecked={useHighLevel}
                    name="highLevel"
                    onToggle={() => setUseHighLevel(!useHighLevel)}
                    label="I use HighLevel"
                    labelHelpTextClassName="sign-up-form__agency_checkbox-label"
                  />
                )}
                <div
                  onClick={() =>
                    setIsSmsMarketingChecked(!isSmsMarketingChecked)
                  }
                  className="welcome-page__sms-marketing"
                >
                  <Checkbox
                    name="sms_marketing"
                    isChecked={isSmsMarketingChecked}
                    className="sign-up-form__sms-marketing-checkbox"
                  />
                  <span className="sign-up-form__sms-marketing-label">
                    <span>Agree to receive SMS marketing messages.</span>
                    <span>
                      Please note that you will receive SMS marketing messages
                      through our website, not by text message. Standard message
                      rates may apply. You can unsubscribe anytime by replying
                      STOP.
                    </span>
                  </span>
                </div>
              </div>

              <input
                className="user-info__submit-input"
                type="submit"
                disabled={getIsValid(formProps)}
              />
              <CompleteRegisterButton
                buttonName="Next"
                className={classNames(
                  'welcome-page__complete-register-button',
                  {
                    'welcome-page__complete-register-button--disabled':
                      getIsValid(formProps),
                  },
                )}
                disabled={getIsValid(formProps)}
                isMobileApp={isAndroid || isIOS}
                onSentToMessenger={formProps.handleSubmit}
              />
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};
